'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { Card } from '../common/Card'
import { FadeIn } from '../motion/FadeIn'
import { StaggerChildren } from '../motion/StaggerChildren'

interface BentoItem {
  title: string
  description?: string
  screenshot?: string
  icon?: React.ReactNode
  size: 'sm' | 'md' | 'lg'
  href?: string
}

interface FeatureBentoProps {
  sectionNumber?: string
  heading: string
  subheading?: string
  items: BentoItem[]
}

const sizeClasses = {
  sm: 'col-span-1',
  md: 'col-span-1 md:col-span-2',
  lg: 'col-span-1 md:col-span-2 lg:col-span-3',
}

const heightClasses = {
  sm: 'min-h-48',
  md: 'min-h-64',
  lg: 'min-h-80',
}

export function FeatureBento({
  sectionNumber,
  heading,
  subheading,
  items
}: FeatureBentoProps) {
  return (
    <Section number={sectionNumber}>
      <Container>
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="headline-lg mb-4">{heading}</h2>
          </FadeIn>
          {subheading && (
            <FadeIn delay={0.1}>
              <p className="body-lg">{subheading}</p>
            </FadeIn>
          )}
        </div>

        {/* Bento Grid */}
        <StaggerChildren
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          staggerDelay={0.06}
        >
          {items.map((item, index) => (
            <Card
              key={index}
              href={item.href}
              className={cn(
                sizeClasses[item.size],
                heightClasses[item.size],
                'relative overflow-hidden group'
              )}
            >
              {/* Background Screenshot */}
              {item.screenshot && (
                <div className="absolute inset-0">
                  <img
                    src={item.screenshot}
                    alt=""
                    className="w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--landing-bg-card)] via-[var(--landing-bg-card)]/80 to-transparent" />
                </div>
              )}

              {/* Content */}
              <div className="relative h-full flex flex-col justify-end p-6">
                {item.icon && (
                  <div className="w-10 h-10 mb-4 rounded-lg bg-[var(--accent)]/10 flex items-center justify-center text-[var(--accent)]">
                    {item.icon}
                  </div>
                )}

                <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">
                  {item.title}
                </h3>

                {item.description && (
                  <p className="text-sm text-[var(--landing-text-secondary)] line-clamp-2">
                    {item.description}
                  </p>
                )}
              </div>
            </Card>
          ))}
        </StaggerChildren>
      </Container>
    </Section>
  )
}
